import { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Select from "react-select";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../firebase";
import { getAssets } from "../../services/assets/assetService";
import { HOLDER_TYPES } from "../../constants/holderTypes";
import {
    createTransferRequest,
    createAssetReturnRequest,
} from "../../services/assets/assetTransactionService";
import { ACTIVE_PENDING_WORKFLOW_STATES } from "../../services/assets/workflowStates";

const ALLOWED_STAFF_ROLES = [
    "technician",
    "manager",
    "store",
    "site_engineer",
    "service_coordinator",
    "project_head",
    "supervisor",
];

export default function TransferRequest() {

    const navigate = useNavigate();
    const location = useLocation();

    // ==================================================
    // STATE
    // ==================================================

    const [loading,       setLoading]       = useState(false);
    const [saving,        setSaving]        = useState(false);
    const [assets,        setAssets]        = useState([]);
    const [stores,        setStores]        = useState([]);
    const [sites,         setSites]         = useState([]);
    const [persons,       setPersons]       = useState([]);
    const [error,         setError]         = useState("");
    const [success,       setSuccess]       = useState("");
    const [selectedAsset, setSelectedAsset] = useState(null);
    const [myPendingCount, setMyPendingCount] = useState(0);

    const [formData, setFormData] = useState({
        asset_id:        "",
        from_type:       HOLDER_TYPES.STORE,
        from_id:         "",
        to_type:         HOLDER_TYPES.STAFF,
        to_id:           "",
        remarks:         "",
        carried_by_id:   "",
        carried_by_name: "",
    });

    // ==================================================
    // USER / COMPANY
    // ==================================================

    const user       = JSON.parse(localStorage.getItem("user") || "{}");
    const company_id = localStorage.getItem("company_id") || user?.company_id;

    // ==================================================
    // LOAD MASTER DATA
    // ==================================================

    const loadMasterData = async () => {
        try {
            setLoading(true);

            // ASSETS
            const assetResponse = await getAssets({ company_id });
            const isStoreOrSM   = ["sm", "owner", "manager", "store"].includes(user?.role);

            const activeAssets = (assetResponse || []).filter((item) => {
                if (item.status === "ARCHIVED") return false;
                if (isStoreOrSM) return true;
                const myId = user?.uid || user?.id || "";
                return item?.current_holder_id === myId;
            });
            setAssets(activeAssets);

            // SITES
            const siteSnapshot = await getDocs(query(collection(db, "sites"), where("company_id", "==", company_id)));
            const siteList = siteSnapshot.docs
                .map((doc) => ({ id: doc.id, ...doc.data() }));
            setSites(siteList);

            // USERS
            const userSnapshot = await getDocs(query(collection(db, "users"), where("company_id", "==", company_id)));
            const userList = userSnapshot.docs
                .map((doc) => ({ id: doc.id, ...doc.data() }));
            setPersons(userList);

            // STORES
            setStores([
                { id: "MAIN_STORE", name: "MAIN STORE" },
                { id: "TOOL_ROOM",  name: "TOOL ROOM"  },
            ]);

        } catch (err) {
            console.error(err);
            setError(err.message || "Failed to load data");
        } finally {
            setLoading(false);
        }
    };

    // ==================================================
    // LOAD MY PENDING TRANSFERS COUNT
    // Self-explanatory next-step visibility — only MY transfers
    // (sender OR receiver), never another tech's data.
    // ==================================================

    const loadMyPendingCount = async () => {
        try {
            const myId = String(user?.uid || user?.id || "").trim();
            if (!myId || !company_id) return;

            const q = query(
                collection(db, "asset_transactions"),
                where("company_id",     "==", company_id),
                where("workflow_state", "in", ACTIVE_PENDING_WORKFLOW_STATES)
            );
            const snap = await getDocs(q);

            const mine = snap.docs.filter((d) => {
                const t = d.data();
                return t.from_id === myId || t.to_id === myId;
            });

            setMyPendingCount(mine.length);
        } catch (err) {
            console.error("[TransferRequest] pending count failed:", err);
        }
    };

    useEffect(() => {
        loadMasterData();
        loadMyPendingCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Prefill asset from navigation state (from AssetMaster "Transfer This Asset" button)
    useEffect(() => {
        const prefillName = location.state?.prefill_asset_name;
        if (!prefillName || assets.length === 0) return;
        const match = assets.find(a =>
            (a.asset_name || "").toLowerCase().trim() === prefillName.toLowerCase().trim()
        );
        if (match) {
            setSelectedAsset(match);
            setFormData(prev => ({
                ...prev,
                asset_id:  match.id,
                from_type: match.current_holder_type || HOLDER_TYPES.STORE,
                from_id:   match.current_holder_id   || "",
                to_id:     "",
            }));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assets, location.state]);

    // ==================================================
    // HELPERS
    // ==================================================

    const getPersonDisplayName = (person) => {
        if (!person) return "";
        return (
            person.name          ||
            person.display_name  ||
            person.employee_name ||
            person.email?.split("@")[0] ||
            person.uid           ||
            person.id            ||
            "Unknown Staff"
        );
    };

    // ==================================================
    // HANDLE CHANGE
    // ==================================================

    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === "asset_id") {
            const asset = assets.find((item) => item.id === value);
            if (asset) {
                setSelectedAsset(asset);
                setFormData((prev) => ({
                    ...prev,
                    asset_id:  value,
                    from_type: asset?.current_holder_type || asset?.holder_type || HOLDER_TYPES.STORE,
                    from_id:   asset?.current_holder_id   || asset?.holder_id   || asset?.assigned_to ||
                               asset?.issued_to           || asset?.current_holder || asset?.holder_uid || "",
                    to_id: "",
                }));
            }
            return;
        }

        if (name === "to_type") {
            setFormData((prev) => ({ ...prev, to_type: value, to_id: "" }));
            return;
        }

        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    // ==================================================
    // GET TARGET OPTIONS
    // ==================================================

    const getTargetOptions = () => {

        if (formData.to_type === HOLDER_TYPES.STORE) {
            return stores.map((store) => ({
                value: store.id,
                label: store.name,
            }));
        }

        if (formData.to_type === HOLDER_TYPES.SITE) {
            return sites.map((site) => ({
                value: site.id,
                label: site.site_name,
            }));
        }

        const normalizedHolderType = String(formData.to_type || "").trim().toUpperCase();

        if (
            normalizedHolderType === HOLDER_TYPES.STAFF ||
            normalizedHolderType === "TECHNICIAN"       ||
            normalizedHolderType === "STAFF"            ||
            normalizedHolderType === "MANAGER"
        ) {
            // logged-in user ki saari IDs — fail-proof self-exclude
            const myUid   = String(user?.uid   || "").trim().toLowerCase();
            const myId    = String(user?.id    || "").trim().toLowerCase();
            const myEmail = String(user?.email || "").trim().toLowerCase();
            const myName  = String(user?.name  || "").trim().toLowerCase();

            return persons
                .filter((person) => {
                    // active only
                    if (person.active !== true) return false;

                    // operation roles only
                    const role = String(person.role || "").trim().toLowerCase();
                    if (!ALLOWED_STAFF_ROLES.includes(role)) return false;

                    // exclude logged-in user — uid, docId, email, name
                    const pUid   = String(person.uid   || "").trim().toLowerCase();
                    const pId    = String(person.id    || "").trim().toLowerCase();
                    const pEmail = String(person.email || "").trim().toLowerCase();
                    const pName  = String(person.name  || "").trim().toLowerCase();

                    if (myUid   && pUid   === myUid)   return false;
                    if (myId    && pId    === myId)    return false;
                    if (myEmail && pEmail === myEmail) return false;
                    if (myName  && pName  === myName)  return false;

                    return true;
                })
                .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
                .map((person) => ({
                    value: person.uid || person.id,
                    label: getPersonDisplayName(person),
                }));
        }

        return [];
    };

    // ==================================================
    // SUBMIT
    // ==================================================

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            setSaving(true);
            setError("");
            setSuccess("");

            // VALIDATION
            if (!formData.asset_id) throw new Error("Select asset");
            if (!formData.to_id)    throw new Error("Select target holder");
            if (formData.to_type === HOLDER_TYPES.SITE && !formData.carried_by_id) {
                throw new Error("Please select who is carrying this asset to the site");
            }

            // Self-transfer check — normalize uid + docId
            const fromIdNorm   = String(formData.from_id || "").trim().toLowerCase();
            const toIdNorm     = String(formData.to_id   || "").trim().toLowerCase();
            const targetPerson = persons.find((p) =>
                String(p.uid || "").trim().toLowerCase() === toIdNorm ||
                String(p.id  || "").trim().toLowerCase() === toIdNorm
            );
            const targetUid = String(targetPerson?.uid || "").trim().toLowerCase();
            const targetId  = String(targetPerson?.id  || "").trim().toLowerCase();

            const isSameHolder =
                formData.from_type === formData.to_type &&
                (fromIdNorm === toIdNorm || fromIdNorm === targetUid || fromIdNorm === targetId);

            if (isSameHolder) {
                throw new Error("Cannot transfer asset to the same person");
            }

            // Technician can only transfer their own asset
            const isTechRole = ["technician", "tech"].includes(
                String(user?.role || "").trim().toLowerCase()
            );
            if (isTechRole) {
                const myId = String(user?.uid || user?.id || "").trim().toLowerCase();
                if (myId !== fromIdNorm) {
                    throw new Error("You can only transfer assets currently in your possession");
                }
            }

            // NORMALIZE TYPES
            const normalizedFromType = String(formData.from_type || "").trim().toUpperCase();
            const normalizedToType   = String(formData.to_type   || "").trim().toUpperCase();

            // RETURN WORKFLOW — person → store
            const isReturnWorkflow =
                [HOLDER_TYPES.STAFF, "TECHNICIAN", "EMPLOYEE"].includes(normalizedFromType) &&
                normalizedToType === HOLDER_TYPES.STORE;

            if (isReturnWorkflow) {
                const returnFromPerson = persons.find((p) => (p.uid || p.id) === formData.from_id);
                const returnFromSite   = sites.find((s) => s.id === formData.from_id);
                const returnFromHolderName =
                    returnFromPerson?.name || returnFromSite?.site_name || formData.from_id;

                await createAssetReturnRequest({
                    asset_id:          formData.asset_id,
                    from_type:         normalizedFromType,
                    from_id:           formData.from_id,
                    from_holder_name:  returnFromHolderName,
                    requested_by:      user?.uid || user?.id || "SYSTEM",
                    requested_by_name: user?.name || user?.display_name || user?.email?.split("@")[0] || "SYSTEM",
                    remarks:           formData.remarks || "",
                    company_id,
                });

            } else {
                // NORMAL TRANSFER
                const selectedTarget       = targetOptions.find((opt) => opt.value === formData.to_id);
                const resolvedToHolderName = selectedTarget?.label || formData.to_id;

                const fromPerson = persons.find((p) => (p.uid || p.id) === formData.from_id);
                const fromSite   = sites.find((s) => s.id === formData.from_id);
                const fromStore  = stores.find((s) => s.id === formData.from_id);
                const resolvedFromHolderName =
                    fromPerson?.name || fromSite?.site_name || fromStore?.name || formData.from_id;

                // Use already-resolved targetPerson for Auth UID
                const resolvedToUid = String(targetPerson?.uid || "").trim();

                await createTransferRequest({
                    asset_id:          formData.asset_id,
                    from_type:         normalizedFromType,
                    from_id:           formData.from_id,
                    from_holder_name:  resolvedFromHolderName,
                    to_type:           normalizedToType,
                    to_id:             formData.to_id,
                    to_uid:            resolvedToUid,   // ← NEW
                    to_holder_name:    resolvedToHolderName,
                    requested_by:      user?.uid || user?.id || "SYSTEM",
                    requested_by_name: user?.name || user?.display_name || user?.email?.split("@")[0] || "SYSTEM",
                    carried_by_id:     formData.carried_by_id   || "",
                    carried_by_name:   formData.carried_by_name || "",
                    remarks:           formData.remarks || "",
                    company_id,
                });
            }

            setSuccess("Transfer request created successfully");

            // RESET
            setFormData({
                asset_id:        "",
                from_type:       HOLDER_TYPES.STORE,
                from_id:         "",
                to_type:         HOLDER_TYPES.STAFF,
                to_id:           "",
                remarks:         "",
                carried_by_id:   "",
                carried_by_name: "",
            });
            setSelectedAsset(null);

        } catch (err) {
            console.error(err);
            setError(err.message || "Failed to create transfer request");
        } finally {
            setSaving(false);
        }
    };

    // ==================================================
    // OPTIONS
    // ==================================================

    const targetOptions = useMemo(() => {
        return getTargetOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.to_type, persons, stores, sites]);

    // ==================================================
    // UI
    // ==================================================

    return (
        <div style={{ padding: "20px" }}>

            <div style={{ marginBottom: "20px" }}>
                <h2>Transfer Asset</h2>
                <p>HVAC safe asset transfer workflow</p>
            </div>

            {/* ── NEXT-STEP VISIBILITY BANNER — only MY pending transfers ── */}
            {myPendingCount > 0 && (
                <div
                    onClick={() => navigate("/assets/pending")}
                    style={{
                        display: "flex", alignItems: "center", gap: "12px",
                        background: "#fffbeb", border: "1px solid #fde68a",
                        borderRadius: "10px", padding: "12px 16px",
                        marginBottom: "16px", cursor: "pointer",
                    }}
                >
                    <span style={{ fontSize: "18px" }}>⏳</span>
                    <div style={{ flex: 1 }}>
                        <div style={{ fontSize: "14px", fontWeight: "700", color: "#92400e" }}>
                            {myPendingCount} pending transfer{myPendingCount > 1 ? "s" : ""} waiting for action
                        </div>
                        <div style={{ fontSize: "12px", color: "#b45309" }}>
                            Tap to view and accept/reject
                        </div>
                    </div>
                    <span style={{ color: "#b45309", fontSize: "16px" }}>→</span>
                </div>
            )}

            {error && (
                <div style={{
                    background: "#ffe5e5", color: "#d00000",
                    padding: "12px", borderRadius: "8px", marginBottom: "16px",
                }}>
                    {error}
                </div>
            )}

            {success && (
                <div style={{
                    background: "#e7ffe7", color: "#008000",
                    padding: "12px 16px", borderRadius: "8px", marginBottom: "16px",
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    flexWrap: "wrap", gap: "10px",
                }}>
                    <span>✅ {success}</span>
                    <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                        <button
                            onClick={() => navigate("/assets/pending")}
                            style={{ padding: "6px 14px", background: "#f59e0b", color: "#fff", border: "none", borderRadius: "8px", fontWeight: 600, fontSize: 12, cursor: "pointer" }}>
                            ⏳ View Pending Transfers →
                        </button>
                        <button
                            onClick={() => navigate("/assets/returns")}
                            style={{ padding: "6px 14px", background: "#2563eb", color: "#fff", border: "none", borderRadius: "8px", fontWeight: 600, fontSize: 12, cursor: "pointer" }}>
                            ↩️ View My Tools →
                        </button>
                    </div>
                </div>
            )}

            <div style={{
                background: "#ffffff", padding: "20px",
                borderRadius: "12px", boxShadow: "0 2px 10px rgba(0,0,0,0.08)",
            }}>
                <form onSubmit={handleSubmit}>

                    <div style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
                        gap: "16px",
                    }}>

                        {/* ASSET SELECT */}
                        <div>
                            <label>Select Asset</label>
                            <Select
                                options={assets.map((asset) => ({
                                    value: asset.id,
                                    label: `${asset.asset_name || "Unnamed Asset"}${asset.serial_no ? ` - ${asset.serial_no}` : ""}`,
                                }))}
                                value={
                                    assets
                                        .map((asset) => ({
                                            value: asset.id,
                                            label: `${asset.asset_name || "Unnamed Asset"}${asset.serial_no ? ` - ${asset.serial_no}` : ""}`,
                                        }))
                                        .find((item) => item.value === formData.asset_id) || null
                                }
                                onChange={(selected) => {
                                    const value = selected?.value || "";
                                    const asset = assets.find((item) => item.id === value);
                                    setSelectedAsset(asset || null);
                                    setFormData((prev) => ({
                                        ...prev,
                                        asset_id:  value,
                                        from_type: asset?.current_holder_type || asset?.holder_type || HOLDER_TYPES.STORE,
                                        from_id:   asset?.current_holder_id   || asset?.holder_id   || asset?.assigned_to ||
                                                   asset?.issued_to           || asset?.current_holder || asset?.holder_uid || "",
                                        to_id: "",
                                    }));
                                }}
                                placeholder="Search Asset"
                                isSearchable
                                isClearable
                                menuPlacement="auto"
                                menuPosition="fixed"
                                maxMenuHeight={320}
                                styles={{
                                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                    menuList:   (base) => ({ ...base, maxHeight: "320px", overflowY: "auto" }),
                                }}
                                menuPortalTarget={document.body}
                            />
                        </div>

                        {/* CURRENT HOLDER */}
                        <div>
                            <label>Current Holder</label>
                            <input
                                value={(() => {
                                    const ht = String(formData.from_type || "").trim().toUpperCase();
                                    if (
                                        ht === HOLDER_TYPES.STAFF ||
                                        ht === "TECHNICIAN" || ht === "STAFF" || ht === "MANAGER"
                                    ) {
                                        const lookupId = String(formData.from_id || "").trim().toLowerCase();
                                        const person = persons.find((p) =>
                                            String(p.uid  || "").trim().toLowerCase() === lookupId ||
                                            String(p.id   || "").trim().toLowerCase() === lookupId ||
                                            String(p.email?.split("@")[0] || "").trim().toLowerCase() === lookupId ||
                                            String(p.name || "").trim().toLowerCase() === lookupId
                                        );
                                        return getPersonDisplayName(person);
                                    }
                                    if (formData.from_type === HOLDER_TYPES.SITE) {
                                        const site = sites.find((s) => s.id === formData.from_id);
                                        return site?.site_name || formData.from_id;
                                    }
                                    if (formData.from_type === HOLDER_TYPES.STORE) {
                                        const store = stores.find((s) => s.id === formData.from_id);
                                        return store?.name || formData.from_id;
                                    }
                                    return formData.from_id;
                                })()}
                                disabled
                                style={{ ...inputStyle, background: "#f3f4f6" }}
                            />
                        </div>

                        {/* TO TYPE */}
<div>
    <label>Transfer To</label>
    <select
        name="to_type"
        value={formData.to_type}
        onChange={handleChange}
        style={inputStyle}
    >
        {["sm", "owner", "manager", "store"].includes(user?.role) && (
            <option value={HOLDER_TYPES.STORE}>STORE</option>
        )}
        <option value={HOLDER_TYPES.STAFF}>STAFF</option>
        {/* SITE — sirf RETURNABLE assets ke liye */}
        {["sm", "owner", "manager", "store"].includes(user?.role) &&
         selectedAsset?.ownership_mode !== "PERSONAL" && (
            <option value={HOLDER_TYPES.SITE}>SITE</option>
        )}
    </select>
</div>

                        {/* TARGET HOLDER */}
                        <div>
                            <label>Target Holder</label>
                            <select
                                name="to_id"
                                value={formData.to_id}
                                onChange={handleChange}
                                required
                                style={{
                                    ...inputStyle,
                                    minHeight: "44px", height: "44px",
                                    padding: "0 12px", borderRadius: "8px",
                                    background: "#f3f4f6",
                                }}
                            >
                                <option value="">Select Holder</option>
                                {targetOptions.map((item) => (
                                    <option key={item.value} value={item.value}>
                                        {item.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                    </div>

                    {/* CARRIED BY — sirf site transfer pe */}
                    {formData.to_type === HOLDER_TYPES.SITE && (
                        <div style={{ marginTop: "16px" }}>
                            <label>Carried By (Tech/Staff) <span style={{ color: "#ef4444" }}>*</span></label>
                            <select
                                name="carried_by_id"
                                value={formData.carried_by_id || ""}
                                onChange={e => {
                                    const person = persons.find(p => (p.uid || p.id) === e.target.value);
                                    setFormData(prev => ({
                                        ...prev,
                                        carried_by_id:   e.target.value,
                                        carried_by_name: person?.name || "",
                                    }));
                                }}
                                style={inputStyle}
                            >
                                <option value="">— Select who is carrying this asset —</option>
                                {persons
                                    .filter(p => p.active === true && ALLOWED_STAFF_ROLES.includes((p.role || "").toLowerCase()))
                                    .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
                                    .map(p => (
                                        <option key={p.uid || p.id} value={p.uid || p.id}>
                                            {p.name} ({p.role})
                                        </option>
                                    ))
                                }
                            </select>
                            <p style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
                                This tech is responsible for the asset at the site.
                            </p>
                        </div>
                    )}

                    {/* REMARKS */}
                    <div style={{ marginTop: "16px" }}>
                        <label>Remarks</label>
                        <textarea
                            name="remarks"
                            value={formData.remarks}
                            onChange={handleChange}
                            rows="4"
                            style={{ ...inputStyle, resize: "vertical" }}
                        />
                    </div>

                    {/* ASSET INFO */}
                    {selectedAsset && (
                        <div style={{
                            marginTop: "20px", padding: "16px",
                            borderRadius: "10px", background: "#f9fafb",
                            border: "1px solid #e5e7eb",
                        }}>
                            <h4>Asset Information</h4>
                            <div style={{
                                display: "grid",
                                gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                                gap: "12px",
                            }}>
                                <InfoItem label="Asset"     value={selectedAsset.asset_name}     />
                                <InfoItem label="Category"  value={selectedAsset.asset_category} />
                                <InfoItem label="Serial"    value={selectedAsset.serial_no}      />
                                <InfoItem label="Ownership" value={selectedAsset.ownership_mode} />
                                <InfoItem label="Status"    value={selectedAsset.status}         />
                            </div>
                        </div>
                    )}

                    {/* SUBMIT */}
                    <button
                        type="submit"
                        disabled={saving || loading}
                        style={{
                            marginTop: "20px", background: "#1e40af",
                            color: "#ffffff", border: "none",
                            padding: "12px 20px", borderRadius: "8px",
                            cursor: "pointer", fontWeight: "600",
                        }}
                    >
                        {saving ? "Submitting..." : "Create Transfer Request"}
                    </button>

                </form>
            </div>
        </div>
    );
}

// ==================================================
// INFO ITEM
// ==================================================

function InfoItem({ label, value }) {
    return (
        <div>
            <div style={{ fontSize: "12px", color: "#6b7280", marginBottom: "4px" }}>
                {label}
            </div>
            <div style={{ fontWeight: "600" }}>
                {value || "-"}
            </div>
        </div>
    );
}

// ==================================================
// INPUT STYLE
// ==================================================

const inputStyle = {
    width: "100%",
    minHeight: "44px",
    height: "44px",
    padding: "0 12px",
    marginTop: "6px",
    borderRadius: "8px",
    border: "1px solid #d1d5db",
    boxSizing: "border-box",
};